import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { Unsubscribe } from 'router5/dist/types/base';
import { IRouter } from '../../infrastructure/router';
import { IJobDescriptionDTO, IUploadThemeArchiveJobDetails, IUploadThemeArchiveJobResult, JobStatus, JobType } from '../../api/jobs.dao';
import { RouteParams, Routes } from '../../routes-table';
import { IAlertsService } from '../../infrastructure/alerts';
import { IErrorParsing } from '../../infrastructure/error-parsing';
import { IModuleVM } from '../../infrastructure/components/module-vm';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../infrastructure/sub-windows';
import { IThemeDTO, IThemesDAO } from '../../api/themes.dao';
import { IJobsMonitor } from '../../domain/jobs-monitor/jobs-monitor';
import { ThemeForm } from './_theme-form/theme-form';


class ThemesVM implements IModuleVM {
    @mobx.observable
    public themesList: IThemeDTO[];
    @mobx.observable
    public isListLoading: boolean;
    @mobx.observable
    private uploadPassportThemeStatus: boolean;
    private uploadPassportThemeStatuses: number[];
    @mobx.observable
    private themeToEditPopupMap: { [key: number]: ISubWindowFacade } = {};

    private subscriptions: Unsubscribe[];

    constructor(
        @IoC.Inject private readonly themesDAO: IThemesDAO = IoC.Container.get(IThemesDAO),
        @IoC.Inject private readonly router: IRouter<RouteParams> = IoC.Container.get(IRouter),
        @IoC.Inject private readonly jobsMonitor: IJobsMonitor = IoC.Container.get(IJobsMonitor),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
    ) {
        this.uploadPassportThemeStatus = false;
        this.uploadPassportThemeStatuses = [];
    }

    public getThemeList(): IThemeDTO[] {
        return this.themesList;
    }

    didMount(): void {
        this.subscriptions = [];

        this.subscriptions.push(this.router.listen(Routes.ThemesList, async () => {
            this.initialize();
            await this.fetchThemeList();
        }));
    }

    willUnmount(): void {
        this.subscriptions.forEach(x => x());
        this.subscriptions = [];
    }

    @mobx.action
    public async initialize() {
        this.jobsMonitor.registerHandler(JobType.UploadPassportTheme, this.handleUploadPassportTheme);
    }

    public cleanup = () => {
        this.jobsMonitor.removeHandler(JobType.UploadPassportTheme, this.handleUploadPassportTheme);
    };

    private handleUploadPassportTheme = (job: IJobDescriptionDTO<IUploadThemeArchiveJobDetails, IUploadThemeArchiveJobResult>) => {
        if (this.uploadPassportThemeStatuses.indexOf(job.jobId) === -1) {
            this.handleUploadThemeArchiveJobStatusChange(job);
        }
    };

    @mobx.action
    private handleUploadThemeArchiveJobStatusChange = async (job: IJobDescriptionDTO<IUploadThemeArchiveJobDetails, IUploadThemeArchiveJobResult>) => {
        if (job.jobStatus === JobStatus.InProgress) {
            this.uploadPassportThemeStatus = true;
        } else {
            if (this.uploadPassportThemeStatuses.indexOf(job.jobId) === -1) {
                this.uploadPassportThemeStatus = false;
                await this.fetchThemeList();
                this.uploadPassportThemeStatuses.push(job.jobId);
            }
        }
    };

    @mobx.action
    public fetchThemeList = async () => {
        this.isListLoading = true;
        let response;

        try {
            response = await this.themesDAO.getThemeList();
            this.onFetchThemesListSuccess(response);
        } catch (error) {
            this.onFetchThemesListError(error);
        } finally {
            mobx.runInAction(() => {
                this.isListLoading = false;
            });
        }
    };

    @mobx.observable
    private searchInputValue: string = '';

    public get themeName(): string {
        return this.searchInputValue;
    }

    @mobx.action.bound
    public setThemeName(value: string): void {
        this.searchInputValue = value.trimLeft();
    }

    @mobx.action.bound
    public async onThemeSearch(themeName: string): Promise<void> {
        if (themeName.trimLeft() === this.themeName) return;

        this.setThemeName(themeName);

        if (this.themeName.length >= 1) {
            await this.fetchThemeListByName(this.themeName);
        } else {
            await this.onThemeSearchReset();
        }
    }

    @mobx.action.bound
    public async onThemeSearchReset(): Promise<void> {
        this.setThemeName('');
        await this.fetchThemeList();
    }

    @mobx.action.bound
    private async fetchThemeListByName(name: string = ''): Promise<void> {
        this.isListLoading = true;
        try {
            const response = await this.themesDAO.getThemeListByName(name);
            this.onFetchThemesListSuccess(response);
        } catch (e) {
            this.onFetchThemesListError(e);
        } finally {
            mobx.runInAction(() => {
                this.isListLoading = false;
            });
        }
    }

    @mobx.action
    private onFetchThemesListSuccess(response: IThemeDTO[]): void {
        this.themesList = response;
    }

    @mobx.action
    private onFetchThemesListError(error): void {
        this.alertsService.alert({
            message: this.errorParsingService.extractMessage(error)
        });
    }

    public showThemeForm = (title: string, themeId?: number) => {
        return this.subWindowService.showSubWindow({
            componentToHost: ThemeForm,
            componentToHostProps: {
                fetchThemeList: this.fetchThemeList,
                theme: this.themesList.find(el => el.id === themeId)
            },
            title: title,
        });
    };

    public showEditTheme = async (title: string, themeId: number) => {
        let shallShowNewEditWindow = true;

        const alreadyShownSubWindow = this.themeToEditPopupMap[themeId];

        if (alreadyShownSubWindow && alreadyShownSubWindow.getStatus() !== SubWindowStatus.Closed) {
            alreadyShownSubWindow.maximize();
            shallShowNewEditWindow = false;
        }

        if (shallShowNewEditWindow) {
            mobx.runInAction(() => {
                this.themeToEditPopupMap[themeId] = this.showThemeForm(title, themeId);
            });
        }
    };

    public isThemeEditFormShown = (themeId: number) => {
        return this.themeToEditPopupMap[themeId]
            && this.themeToEditPopupMap[themeId].getStatus() !== SubWindowStatus.Closed;
    };
}

export { ThemesVM };
